import { Check, Minus } from "lucide-react";
import { Reveal, Label } from "./ui-bits";
import { TIERS, PLAN_COMPARISON } from "@/data/pricing";

function Cell({ value }) {
  if (value === true) {
    return (
      <span className="inline-grid place-items-center w-7 h-7 rounded-full bg-teal/10 border border-teal/40" aria-label="Included">
        <Check size={14} className="text-teal" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-grid place-items-center w-7 h-7 rounded-full border border-line" aria-label="Not included">
        <Minus size={14} className="text-dusk" />
      </span>
    );
  }
  return <span className="text-sm text-bone/85 leading-snug">{value}</span>;
}

export default function PlanComparison() {
  return (
    <div id="compare" className="scroll-mt-28" data-testid="plan-comparison">
      <Reveal>
        <Label>Compare plans, line by line</Label>
        <p className="text-mute text-sm sm:text-base leading-relaxed mt-4 max-w-2xl">
          Every plan includes the care a clinician deems necessary — the tiers differ in the frequency
          of reviews, breadth of investigation and depth of coordination, not in safety.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <p className="sm:hidden mono-label text-dusk mt-6" data-testid="plan-comparison-swipe-hint">Swipe sideways to compare plans →</p>
        <div className="mt-4 sm:mt-8 overflow-x-auto card-line rounded-2xl">
          <table className="w-full min-w-[760px] text-left border-collapse" data-testid="plan-comparison-table">
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="sticky left-0 bg-surface px-5 py-5 w-[34%] mono-label text-dusk font-normal align-bottom">
                  Feature
                </th>
                {TIERS.map((t) => (
                  <th
                    key={t.name}
                    scope="col"
                    className={`px-5 py-5 align-bottom ${t.popular ? "bg-teal/5" : ""}`}
                    data-testid={`plan-comparison-head-${t.name.toLowerCase()}`}
                  >
                    <p className="mono-label text-dusk">{t.badge}</p>
                    <p className="font-display text-2xl tracking-wide mt-1">{t.name}</p>
                    <p className="mt-1">
                      <span className="font-display text-3xl text-teal">{t.price}</span>
                      <span className="text-dusk text-xs ml-1.5">{t.period}</span>
                    </p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_COMPARISON.map((group) => (
                <GroupRows key={group.group} group={group} />
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <p className="text-xs text-dusk leading-relaxed mt-5 max-w-3xl">
          Proposed launch pricing; plan contents are confirmed in writing before you commit. The
          treatment form on each plan is prescribed only where clinically appropriate. Treatment is subject to individual clinical
          assessment — a plan is never a promise of a prescription.
        </p>
      </Reveal>
    </div>
  );
}

function GroupRows({ group }) {
  return (
    <>
      <tr className="border-b border-line bg-ink/40">
        <th
          scope="rowgroup"
          colSpan={TIERS.length + 1}
          className="sticky left-0 px-5 py-3 mono-label text-teal font-normal text-left"
        >
          {group.group}
        </th>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.label} className="border-b border-line last:border-b-0 hover:bg-white/[0.02] transition-colors">
          <th scope="row" className="sticky left-0 bg-surface px-5 py-4 text-sm text-bone/80 font-normal align-top">
            {row.label}
          </th>
          {row.values.map((v, i) => (
            <td key={i} className={`px-5 py-4 align-top ${TIERS[i].popular ? "bg-teal/5" : ""}`}>
              <Cell value={v} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
